import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { AlertTriangle, Check, Lock, ShieldCheck, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SCOPE_LABELS, useAuthorizeContext } from '../api/oauth'

export function AuthorizeAppPage() {
  const { t } = useTranslation('auth')
  const [params] = useSearchParams()
  const requestId = params.get('request_id')
  const { data, isLoading, isError, error } = useAuthorizeContext(requestId)

  const defaultOrgId = useMemo(() => {
    if (!data) return ''
    const active = data.memberships.find((m) => m.org_id === data.active_org_id)
    return (active ?? data.memberships[0])?.org_id ?? ''
  }, [data])

  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const orgId = selectedOrgId || defaultOrgId

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <header className="border-b border-border bg-surface/60">
        <div className="mx-auto flex max-w-2xl items-center justify-center px-6 py-5">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-background">
              V
            </span>
            Voysi AI
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        {!requestId && (
          <ErrorCard
            title={t('authorize.missingRequestId')}
            detail={t('authorize.missingRequestIdDetail')}
          />
        )}

        {requestId && isLoading && <LoadingCard />}

        {requestId && isError && (
          <ErrorCard
            title={t('authorize.requestUnavailable')}
            detail={
              error instanceof Error
                ? error.message
                : t('authorize.requestExpired')
            }
          />
        )}

        {requestId && data && (
          <form
            action="/oauth/authorize/decision"
            method="POST"
            className="space-y-6 rounded-xl border border-border bg-surface p-6 shadow-lg"
          >
            <input type="hidden" name="request_id" value={requestId} />
            <input type="hidden" name="org_id" value={orgId} />

            <div className="flex items-center gap-4 border-b border-border pb-5">
              <ClientAvatar name={data.client.name} logo={data.client.logo_uri} />
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold">{data.client.name}</h1>
                <p className="text-sm text-text-secondary">
                  {t('authorize.wantsAccess')}
                </p>
              </div>
            </div>

            <RedirectChip host={data.client.redirect_host} uri={data.client.redirect_uri} />

            <section className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {t('authorize.permissionsRequested')}
              </h2>
              <ul className="divide-y divide-border rounded-lg border border-border bg-background">
                {data.requested_scopes.map((scope) => {
                  const label = SCOPE_LABELS[scope]
                  return (
                    <li key={scope} className="flex items-start gap-3 px-4 py-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      <div>
                        <div className="text-sm font-medium">
                          {label?.title ?? scope}
                        </div>
                        {label?.description && (
                          <div className="text-xs text-text-secondary">
                            {label.description}
                          </div>
                        )}
                        <code className="mt-0.5 inline-block font-mono text-[10px] text-text-muted">
                          {scope}
                        </code>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>

            <section className="space-y-2">
              <h2 className="flex items-center gap-2 text-sm font-medium">
                <Users className="h-4 w-4 text-primary" />
                {t('authorize.orgToShare')}
              </h2>
              {data.memberships.length === 0 ? (
                <div className="rounded-lg border border-border bg-background p-4 text-sm text-text-secondary">
                  {t('authorize.noOrgsYet')}
                </div>
              ) : (
                <div className="space-y-2">
                  {data.memberships.map((m) => {
                    const checked = orgId === m.org_id
                    return (
                      <label
                        key={m.org_id}
                        className={`flex cursor-pointer items-center justify-between rounded-lg border px-4 py-3 transition ${
                          checked
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-background hover:border-border-light'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="org_radio"
                            className="h-4 w-4 accent-primary"
                            checked={checked}
                            onChange={() => setSelectedOrgId(m.org_id)}
                          />
                          <div>
                            <div className="text-sm font-medium">{m.name}</div>
                            <div className="text-xs text-text-secondary">
                              {t('authorize.roleLabel', { role: m.role })}
                            </div>
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </div>
              )}
            </section>

            <div className="flex flex-col-reverse gap-2 border-t border-border pt-5 sm:flex-row sm:justify-end">
              <button
                type="submit"
                name="decision"
                value="deny"
                className="rounded-lg border border-border bg-surface px-4 py-2 text-sm text-text-secondary hover:bg-surface-elevated"
              >
                {t('common:actions.deny')}
              </button>
              <button
                type="submit"
                name="decision"
                value="approve"
                disabled={!orgId}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-background hover:bg-primary-hover disabled:opacity-50"
              >
                {t('common:actions.approve')}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  )
}

function ClientAvatar({ name, logo }: { name: string; logo: string | null }) {
  if (logo) {
    return (
      <img
        src={logo}
        alt=""
        className="h-14 w-14 rounded-xl border border-border object-cover"
      />
    )
  }
  const initial = (name.trim()[0] ?? '?').toUpperCase()
  return (
    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-surface-elevated text-xl font-semibold text-text-primary">
      {initial}
    </div>
  )
}

function RedirectChip({ host, uri }: { host: string; uri: string }) {
  const { t } = useTranslation('auth')
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border bg-background px-4 py-3">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-text-secondary" />
      <div className="min-w-0 text-xs">
        <div className="text-text-secondary">{t('authorize.afterApproval')}</div>
        <div className="mt-0.5 font-mono">
          <span className="font-bold text-text-primary">{host}</span>
          <span className="text-text-muted">{uri.replace(new RegExp(`^https?://${host}`), '')}</span>
        </div>
      </div>
    </div>
  )
}

function LoadingCard() {
  return (
    <div className="animate-pulse space-y-4 rounded-xl border border-border bg-surface p-6">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-xl bg-surface-elevated" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/2 rounded bg-surface-elevated" />
          <div className="h-3 w-1/3 rounded bg-surface-elevated" />
        </div>
      </div>
      <div className="h-12 rounded bg-surface-elevated" />
      <div className="h-24 rounded bg-surface-elevated" />
      <div className="h-20 rounded bg-surface-elevated" />
    </div>
  )
}

function ErrorCard({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-error/50 bg-error/10 p-5">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-error" />
      <div>
        <div className="font-medium text-error">{title}</div>
        <p className="mt-1 text-sm text-text-secondary">{detail}</p>
      </div>
    </div>
  )
}
